import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [role, setRole] = useState("patient");
  const [form, setForm] = useState({ name: "", email: "", password: "", specialty: "", bio: "" });
  const [conds, setConds] = useState([]);
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/meta/neurodivergences").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const toggle = (c) => setConds((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register({ ...form, role, neurodivergence: conds });
      toast.success("Conta criada! Bem-vinda(o) à Optia.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="container-optia py-12">
      <div className="max-w-2xl mx-auto">
        <h1 className="font-display text-3xl font-semibold text-navy-900 text-center">Criar sua conta</h1>
        <p className="text-slate-600 text-center mt-2">É rápido e você pode preencher o resto depois.</p>
        <Card className="card-soft mt-8">
          <CardContent className="p-7">
            <form onSubmit={submit} className="space-y-5">
              <div>
                <Label className="mb-2 block">Você é</Label>
                <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3">
                  <label className={`border rounded-xl p-4 cursor-pointer transition ${role==="patient" ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}>
                    <RadioGroupItem value="patient" className="sr-only" data-testid="reg-role-patient" />
                    <div className="font-medium text-navy-900">Paciente</div>
                    <div className="text-xs text-slate-600 mt-1">Busco atendimento</div>
                  </label>
                  <label className={`border rounded-xl p-4 cursor-pointer transition ${role==="professional" ? "border-teal-500 bg-teal-50" : "border-slate-200"}`}>
                    <RadioGroupItem value="professional" className="sr-only" data-testid="reg-role-professional" />
                    <div className="font-medium text-navy-900">Profissional</div>
                    <div className="text-xs text-slate-600 mt-1">Ofereço atendimento</div>
                  </label>
                </RadioGroup>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Nome completo</Label>
                  <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} required data-testid="reg-name" />
                </div>
                <div className="space-y-1.5">
                  <Label>E-mail</Label>
                  <Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} required data-testid="reg-email" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Senha (mín. 6)</Label>
                <Input type="password" minLength={6} value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} required data-testid="reg-password" />
              </div>

              {role === "professional" && (
                <>
                  <div className="space-y-1.5">
                    <Label>Especialidade</Label>
                    <Input placeholder="Ex: Psiquiatria, Psicologia, T.O., Fonoaudiologia" value={form.specialty} onChange={(e)=>setForm({...form, specialty: e.target.value})} data-testid="reg-specialty" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Mini bio</Label>
                    <Textarea value={form.bio} onChange={(e)=>setForm({...form, bio: e.target.value})} rows={3} placeholder="Apresente-se em poucas linhas: sua trajetória, abordagem e o que você oferece à comunidade neurodivergente." data-testid="reg-bio" />
                  </div>
                </>
              )}

              <div>
                <Label className="mb-2 block">{role === "patient" ? "Você se identifica com (opcional):" : "Áreas de expertise:"}</Label>
                <div className="flex flex-wrap gap-2" data-testid="reg-conditions">
                  {options.map((c) => (
                    <button type="button" key={c} onClick={() => toggle(c)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${conds.includes(c) ? "bg-teal-500 border-teal-500 text-white" : "bg-white border-slate-300 text-slate-700 hover:border-teal-400"}`}
                      data-testid={`reg-cond-${c}`}>{c}</button>
                  ))}
                </div>
              </div>

              <Button type="submit" disabled={busy} className="w-full bg-teal-500 hover:bg-teal-600 h-11 rounded-xl" data-testid="reg-submit">
                {busy ? "Criando..." : "Criar conta"}
              </Button>
              <p className="text-center text-sm text-slate-600">
                Já tem cadastro? <Link to="/login" className="text-teal-700 font-medium hover:underline">Entrar</Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
